import useDimensions from "react-cool-dimensions"
import "./FloatingBoxes.scss"
import cn from "classnames"
import { divideIntoRows } from "@/utils/arrayUtils"
import { SortableContainer, SortableElement } from "react-sortable-hoc"

interface IFloatingBoxesProps<T> {
  minWidth: number
  spacing: number
  items: T[]
  renderBox: (item: T) => JSX.Element
  isSortable?: boolean
  onSortChange?: (fromIndex: number, toIndex: number) => Promise<void>
}

export const FloatingBoxes = <T extends {}>({
  items,
  renderBox,
  minWidth,
  spacing,
  isSortable,
  onSortChange,
}: IFloatingBoxesProps<T>): JSX.Element => {
  const { observe, width } = useDimensions({})

  const perRow = Math.floor(width / (minWidth + spacing))
  const widthPerBox = (width - (perRow - 1) * spacing) / perRow
  const rows = divideIntoRows(items, perRow)

  const Box = ({ item, index }: { item: T; index: number }) => {
    const rowIndex = rows.findIndex((r) => r.find((it) => it === item))
    const row = rows[rowIndex]
    const indexInRow = row?.indexOf(item) ?? 0
    return (
      <div
        key={index}
        className={cn("floating-boxes__box", indexInRow % 2 === 0 && "floating-boxes__box--even")}
        style={{
          width: isNaN(widthPerBox) ? 0 : widthPerBox,
          marginLeft: indexInRow > 0 ? spacing / 2 : 0,
          marginRight: indexInRow < perRow - 1 ? spacing / 2 : 0,
          marginBottom: rowIndex < rows.length - 1 ? spacing : 0,
        }}
      >
        {renderBox(item)}
      </div>
    )
  }

  const SortableBox = SortableElement<{ item: T }>(Box)
  const SortableList = SortableContainer(() => {
    return (
      <div className="floating-boxes__sort-container">
        {items.map((value, index) => (
          <SortableBox key={index} index={index} item={value} />
        ))}
      </div>
    )
  })

  return (
    <div className="floating-boxes" ref={observe}>
      {isSortable && (
        <SortableList
          axis="xy"
          onSortEnd={(sort) => {
            onSortChange?.(sort.oldIndex, sort.newIndex)
          }}
        />
      )}
      {!isSortable && (
        <div className="floating-boxes__no-sort-container">
          {items.map((value, index) => (
            <Box key={index} index={index} item={value} />
          ))}
        </div>
      )}
    </div>
  )
}
